<template lang="pug">
.regras
  header.regras__hero
    .regras__hero-grid
    .regras__hero-glow.regras__hero-glow--blue
    .regras__hero-glow.regras__hero-glow--red
    .regras__hero-inner
      p.regras__eyebrow
        span.regras__eyebrow-pip
        | Documentos oficiais
      h1.regras__title Regras de Combate
      p.regras__sub Os regulamentos da LBR, abertos aqui mesmo. Escolha um documento na lista para ler.

  .regras__wrap
    .regras__layout
      //- ---------- LISTA DE DOCUMENTOS ----------
      aside.regras__list
        button.doc(
          v-for="doc in documentos"
          :key="doc.file"
          type="button"
          :class="{ 'doc--on': doc.file === atual.file }"
          @click="doSelect(doc)"
        )
          span.doc__ic
            FontAwesomeIcon(:icon="['far', 'file-pdf']")
          span.doc__body
            span.doc__name {{ doc.name }}
            span.doc__meta {{ doc.pages }} páginas · PDF
          FontAwesomeIcon.doc__chevron(:icon="['fas', 'chevron-right']")

      //- ---------- VISUALIZADOR ----------
      section.regras__viewer
        .regras__viewer-bar
          div
            h2.regras__viewer-title {{ atual.name }}
            p.regras__viewer-meta(v-if="atual.updatedat")
              FontAwesomeIcon(:icon="['far', 'clock']")
              | Enviado em {{ fmtData(atual.updatedat) }}
          .regras__viewer-actions
            a.regras__btn(:href="url" target="_blank" rel="noopener")
              FontAwesomeIcon(:icon="['fas', 'external-link-alt']")
              span Abrir em nova aba
            a.regras__btn.regras__btn--ghost(:href="url" :download="atual.file")
              FontAwesomeIcon(:icon="['fas', 'download']")
              span Baixar
            //- Só Administrador (papel 1) troca regulamento: é regra oficial da
            //- liga, não configuração de evento. Mesmo gate do backend.
            label.regras__btn.regras__btn--upload(v-if="isAdmin" :class="{ 'is-busy': enviando }")
              FontAwesomeIcon(:icon="['fas', enviando ? 'circle-notch' : 'upload']" :spin="enviando")
              span {{ enviando ? 'Enviando...' : (atual.uploaded ? 'Substituir PDF' : 'Enviar PDF') }}
              input.regras__file(type="file" accept="application/pdf" :disabled="enviando" @change="doUpload")

        p.regras__alert(v-if="erro")
          FontAwesomeIcon(:icon="['fas', 'exclamation-triangle']")
          | {{ erro }}
        p.regras__alert.regras__alert--ok(v-if="okMsg")
          FontAwesomeIcon(:icon="['fas', 'check-circle']")
          | {{ okMsg }}

        //- O :key força recriar o iframe ao trocar de documento: sem isso o
        //- visualizador nativo do Chrome mantém o PDF antigo em cache.
        .regras__frame
          iframe.regras__iframe(:key="atual.file" :src="url" :title="atual.name")
          p.regras__fallback
            | Se o documento não aparecer aqui, seu navegador não abre PDF embutido (comum no celular).
            a(:href="url" target="_blank" rel="noopener") Abra em nova aba.
</template>

<script>
import { mapGetters } from 'vuex'
import axios from '@/axios'

// Os PDFs vivem em frontend/public/regulamentos/ e são publicados junto com o
// build. O nginx serve o arquivo real antes do fallback da SPA
// (try_files $uri ...), então o caminho absoluto entrega o PDF, não o index.html.
//
// A pasta NÃO pode se chamar "regras": em filesystem case-insensitive (Windows,
// no dev) ela casa com a rota /Regras, o servidor redireciona para /Regras/ e,
// como o publicPath é './', os scripts passam a ser buscados em /Regras/js/ e a
// aplicação não sobe.
//
// O caminho é absoluto de propósito, pelo mesmo motivo: relativo ('./') quebra
// se a URL ganhar barra final.
// Os documentos que acompanham o build. Enquanto ninguém tiver enviado um PDF
// pela interface, é este arquivo que a tela mostra.
// `uploaded` e `updatedat` já nascem declarados: no Vue 2, propriedade
// acrescentada depois a um objeto reativo não dispara recomputação, e o iframe
// continuaria apontando para o arquivo estático mesmo após o upload.
const DOCUMENTOS = [
  { slug: 'regras-combate', name: 'Regras de Combate', file: 'regras-combate-lbr.pdf', pages: 18, uploaded: false, updatedat: '' },
  { slug: 'criterio-julgamento', name: 'Critério para Julgamento dos Rounds', file: 'criterio-julgamento-lbr.pdf', pages: 11, uploaded: false, updatedat: '' }
]

// O nginx corta corpo acima de ~1MB com 502 e o base64 infla o arquivo em ~33%,
// então 700KB de PDF já viram ~933KB de requisição. Barramos aqui para o
// organizador ver uma mensagem clara em vez de um erro de gateway.
const MAX_PDF = 700 * 1024

export default {
  name: 'Regras',
  data () {
    const documentos = DOCUMENTOS.map(d => ({ ...d }))
    return {
      documentos,
      // referência para dentro da lista, não cópia: assim o que carregarEnviados
      // marcar em `documentos` reflete no documento aberto.
      atual: documentos[0],
      enviando: false,
      erro: '',
      okMsg: '',
      // muda a cada upload para o iframe recarregar em vez de servir o cache
      versao: 0
    }
  },
  created () {
    this.carregarEnviados()
  },
  computed: {
    ...mapGetters('users', ['getUser']),
    // Administrador (1). Organizador não troca regra oficial da liga.
    isAdmin () {
      const roles = this.getUser.roles
      return Array.isArray(roles) && roles.includes(1)
    },
    url () {
      // enviado pela interface -> vem do banco; senão, o arquivo do build
      if (this.atual.uploaded) {
        return '/api/rule_document.pdf?slug=' + this.atual.slug + '&v=' + this.versao
      }
      return '/regulamentos/' + this.atual.file
    }
  },
  methods: {
    // Marca quais documentos já têm PDF enviado, para a tela servir do banco.
    carregarEnviados () {
      return axios.rules.list().then((enviados) => {
        if (!Array.isArray(enviados)) return
        for (const env of enviados) {
          const doc = this.documentos.find(d => d.slug === env.slug)
          if (doc) {
            doc.uploaded = true
            doc.updatedat = env.updatedat
            if (env.pages) doc.pages = env.pages
          }
        }
        const atualizado = this.documentos.find(d => d.slug === this.atual.slug)
        if (atualizado) this.atual = atualizado
      }).catch(() => { /* sem versão enviada: segue com os arquivos do build */ })
    },
    doSelect (doc) {
      this.atual = doc
      this.erro = ''
      this.okMsg = ''
    },
    fmtData (iso) {
      const d = new Date(iso)
      return isNaN(d) ? '' : d.toLocaleDateString('pt-BR')
    },
    doUpload (ev) {
      const file = (ev.target.files || [])[0]
      ev.target.value = '' // permite reenviar o mesmo arquivo depois
      if (!file) return
      this.erro = ''
      this.okMsg = ''
      if (file.type !== 'application/pdf') {
        this.erro = 'Envie um arquivo PDF.'
        return
      }
      if (file.size > MAX_PDF) {
        this.erro = `Este PDF tem ${Math.round(file.size / 1024)}KB e o limite é 700KB. Comprima o arquivo antes de enviar.`
        return
      }
      this.enviando = true
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = String(reader.result).split('base64,')[1]
        axios.rules.upload({
          slug: this.atual.slug,
          name: this.atual.name,
          pdf: base64,
          pages: this.atual.pages
        }).then((resp) => {
          if (!resp || resp.success === false) {
            this.erro = (resp && resp.error) || 'Não foi possível enviar o PDF.'
            return
          }
          this.okMsg = 'PDF atualizado. Todos passam a ver esta versão.'
          this.versao += 1
          return this.carregarEnviados()
        }).catch((e) => {
          const resp = e && e.response
          this.erro = (resp && resp.data && resp.data.error) ||
            'Falha ao enviar. Verifique se você é administrador e se o arquivo não é grande demais.'
        }).finally(() => {
          this.enviando = false
        })
      }
      reader.onerror = () => {
        this.erro = 'Não foi possível ler o arquivo.'
        this.enviando = false
      }
      reader.readAsDataURL(file)
    }
  }
}
</script>

<style lang="sass" scoped>
  $blue: #287AED
  $red: #DD3434
  $ink: #0B0E14
  $muted: #5C6673
  $line: rgba(11, 14, 20, 0.10)
  $paper: #FFFFFF
  $page: #F2F4F8
  $display: 'Saira Condensed', 'Lato', sans-serif
  $mono: 'Space Mono', 'Lato', monospace

  .regras
    color: $ink
    background: $page
    min-height: calc(100vh - 64px)

    *
      box-sizing: border-box

    // ---------- HERO ----------
    &__hero
      position: relative
      overflow: hidden
      isolation: isolate
      padding: 26px 0 40px
      background: radial-gradient(circle at 16% 8%, #15233b 0%, $ink 52%, #05070b 100%)
      color: #fff

    &__hero-grid
      position: absolute
      inset: 0
      background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      background-size: 42px 42px
      mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
      -webkit-mask-image: radial-gradient(circle at 72% 6%, #000 0%, transparent 76%)
      z-index: -2

    &__hero-glow
      position: absolute
      width: 360px
      height: 360px
      border-radius: 50%
      filter: blur(110px)
      opacity: 0.5
      z-index: -1
      &--blue
        background: $blue
        top: -170px
        left: -90px
      &--red
        background: $red
        bottom: -190px
        right: -70px

    &__hero-inner
      position: relative
      max-width: 1240px
      margin: 0 auto
      padding: 0 24px

    &__eyebrow
      display: inline-flex
      align-items: center
      gap: 10px
      font-family: $mono
      text-transform: uppercase
      letter-spacing: 0.22em
      font-size: 12px
      font-weight: 700
      color: rgba(255, 255, 255, 0.7)
      margin: 0 0 12px

    &__eyebrow-pip
      width: 8px
      height: 8px
      border-radius: 50%
      background: linear-gradient(135deg, #5AA0FF, $red)
      box-shadow: 0 0 0 4px rgba(90, 160, 255, 0.15)

    &__title
      font-family: $display
      font-size: 42px
      line-height: 1.02
      font-weight: 800
      letter-spacing: -0.01em
      margin: 0

    &__sub
      max-width: 620px
      margin: 12px 0 0
      font-size: 15px
      line-height: 1.5
      color: rgba(255, 255, 255, 0.72)

    // ---------- CORPO ----------
    &__wrap
      max-width: 1240px
      margin: 0 auto
      padding: 32px 24px 64px

    &__layout
      display: grid
      grid-template-columns: 320px 1fr
      gap: 24px
      align-items: start
      @media (max-width: 900px)
        grid-template-columns: 1fr

    &__list
      display: flex
      flex-direction: column
      gap: 12px
      position: sticky
      top: 24px
      @media (max-width: 900px)
        position: static

    // ---------- VISUALIZADOR ----------
    &__viewer
      background: $paper
      border: 1px solid $line
      border-radius: 18px
      overflow: hidden
      box-shadow: 0 6px 20px rgba(11, 14, 20, 0.06)

    &__viewer-bar
      display: flex
      align-items: center
      justify-content: space-between
      gap: 16px
      flex-wrap: wrap
      padding: 16px 20px
      border-bottom: 1px solid $line

    &__viewer-title
      font-family: $display
      font-size: 21px
      font-weight: 700
      margin: 0

    &__viewer-meta
      display: flex
      align-items: center
      gap: 7px
      margin: 4px 0 0
      font-family: $mono
      font-size: 11px
      letter-spacing: 0.04em
      color: $muted

    &__viewer-actions
      display: flex
      align-items: center
      gap: 10px
      flex-wrap: wrap

    &__btn
      display: inline-flex
      align-items: center
      gap: 8px
      padding: 8px 15px
      border-radius: 999px
      border: 1px solid transparent
      background: $blue
      color: #fff
      font-family: $display
      font-size: 14px
      font-weight: 700
      text-decoration: none
      cursor: pointer
      transition: transform .16s ease, box-shadow .16s ease, background .16s ease
      &:hover
        transform: translateY(-1px)
        box-shadow: 0 5px 14px rgba(40, 122, 237, 0.28)
      &--ghost
        background: none
        border-color: $line
        color: $ink
        &:hover
          border-color: rgba(11, 14, 20, 0.28)
          box-shadow: none
      // o upload é um <label> envolvendo o input file, para o clique abrir o
      // seletor sem precisar de ref/JS
      &--upload
        background: $ink
        &:hover
          box-shadow: 0 5px 14px rgba(11, 14, 20, 0.3)
        &.is-busy
          opacity: 0.7
          cursor: progress
          &:hover
            transform: none

    &__file
      position: absolute
      width: 0
      height: 0
      opacity: 0

    &__alert
      display: flex
      align-items: center
      gap: 9px
      margin: 0
      padding: 12px 20px
      border-bottom: 1px solid $line
      background: rgba(230, 81, 0, 0.08)
      color: #E65100
      font-size: 13.5px
      line-height: 1.45
      &--ok
        background: rgba(46, 158, 79, 0.08)
        color: #1F7C3C

    &__frame
      position: relative
      background: #EDF0F5

    &__iframe
      display: block
      width: 100%
      height: calc(100vh - 260px)
      min-height: 480px
      border: none

    &__fallback
      margin: 0
      padding: 12px 20px
      border-top: 1px solid $line
      background: $paper
      font-size: 12.5px
      line-height: 1.45
      color: $muted
      a
        margin-left: 5px
        color: $blue
        font-weight: 700

  // ---------- CARTÃO DE DOCUMENTO ----------
  .doc
    display: flex
    align-items: center
    gap: 13px
    width: 100%
    text-align: left
    padding: 15px 16px
    border-radius: 14px
    border: 1px solid $line
    background: $paper
    cursor: pointer
    font: inherit
    color: inherit
    transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease
    &:hover
      border-color: rgba(40, 122, 237, 0.4)
      transform: translateY(-1px)
    &--on
      border-color: $blue
      box-shadow: 0 0 0 3px rgba(40, 122, 237, 0.12)
      .doc__ic
        background: $blue
        color: #fff
      .doc__chevron
        color: $blue

    &__ic
      display: grid
      place-items: center
      flex-shrink: 0
      width: 40px
      height: 40px
      border-radius: 11px
      background: rgba(221, 52, 52, 0.10)
      color: $red
      font-size: 17px
      transition: background .18s ease, color .18s ease

    &__body
      display: flex
      flex-direction: column
      gap: 3px
      flex: 1
      min-width: 0

    &__name
      font-family: $display
      font-size: 17px
      font-weight: 700
      line-height: 1.2

    &__meta
      font-family: $mono
      font-size: 11px
      letter-spacing: 0.04em
      color: $muted

    &__chevron
      flex-shrink: 0
      font-size: 12px
      color: #C3CBD8
      transition: color .18s ease
</style>
